import { Icon, addCollection } from '@iconify/react';
import { cn } from '../../lib/cn';
import { notoSportCollection } from './notoSportIcons';

// Register the four bundled Noto marks once, at module load. These are embedded
// offline (see notoSportIcons.ts) so they render in the native app without any
// network access — `<Icon icon="noto:…" />` resolves against this collection.
addCollection(notoSportCollection);

type Sport = 'football' | 'cycling' | 'basketball' | 'tennis';
type Size = 'sm' | 'md';

// Chosen marks (Noto Emoji). Football, basketball and tennis are the balls; for
// cycling we use the road-cyclist-in-motion (the plain bicycle read as boring).
const NOTO_ICON: Record<Sport, string> = {
  football: 'noto:soccer-ball',
  cycling: 'noto:person-biking',
  basketball: 'noto:basketball',
  tennis: 'noto:tennis'
};

const DIM: Record<Size, { box: number; radius: number; icon: number }> = {
  md: { box: 46, radius: 14, icon: 30 },
  sm: { box: 38, radius: 12, icon: 25 }
};

/**
 * Sport tile with a full-colour Noto mark. The marks carry their own colours,
 * so they sit on a neutral light tile (a saturated gradient would fight them).
 */
export function SportMarkTile({ sport, size = 'md', className }: { sport: Sport; size?: Size; className?: string }) {
  const d = DIM[size];
  return (
    <div
      className={cn('relative grid shrink-0 place-items-center overflow-hidden', className)}
      style={{
        width: d.box,
        height: d.box,
        borderRadius: d.radius,
        background: '#F1F1F5',
        boxShadow: 'inset 0 0 0 1px rgba(20,20,50,0.07)'
      }}
    >
      <Icon icon={NOTO_ICON[sport]} width={d.icon} height={d.icon} />
    </div>
  );
}

/** Overlapping basketball + tennis tiles for the “more sports” teaser row. */
export function MultiSportMarkStack({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex shrink-0', className)} style={{ width: 66, height: 38 }}>
      <SportMarkTile sport="basketball" size="sm" className="relative z-[1] border-2 border-wash" />
      <SportMarkTile sport="tennis" size="sm" className="relative -ml-[10px] border-2 border-wash" />
    </div>
  );
}

/** Compact cycling mark on the red “starting soon” card, with an urgency flag. */
export function CyclingMarkBadge({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-[14px] bg-[#F1F1F5] shadow-[inset_0_0_0_1px_rgba(20,20,50,0.07)]',
        className
      )}
    >
      <Icon icon={NOTO_ICON.cycling} width={30} height={30} />
      <div className="absolute -right-[5px] -top-[5px] grid h-[11px] w-4 place-items-center rounded-[2px] bg-accent">
        <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
          <rect x="3" y="0" width="2" height="8" fill="#fff" />
          <rect x="0" y="3" width="8" height="2" fill="#fff" />
        </svg>
      </div>
    </div>
  );
}
